import {FC, PropsWithChildren, ReactElement, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '@yosmanyga/di';
import {UnnormalizedProps} from '@yosmanyga/core-style';
import {Anchor, Container, I18NText, Icon, Text, useIcons} from "@yosmanyga/rich-ui";
import {PaymentController} from '../controller';

const Payment = observer(({templates}: PaymentProps) => {
    const icons = useIcons();

    const controller = useService(PaymentController, []);

    useEffect(() => {
        (async () => {
            await controller.load();
        })();
    }, []);

    if (!controller.loaded) {
        return <templates.layout/>;
    }

    if (controller.version === 'premium') {
        return <templates.layout>
            <AlreadyPaid/>
        </templates.layout>;
    }

    return <templates.layout>
        <Container
            // intent="card"
            flow='column'
            align={{
                cross: 'center',
            }}
            margin={{
                top: 4,
            }}
        >
            <I18NText
                intent='title'
                margin={{
                    top: 2
                }}
            >
                Buy Premium version
            </I18NText>

            <I18NText
                margin={{
                    top: 2
                }}
            >
                Have these features the whole game.
            </I18NText>

            <I18NText
                values={{
                    percent: controller.percent
                }}
                intent='secondary'
                margin={{
                    top: 0.5
                }}
            >
                Trial keep them only until percent of the mission.
            </I18NText>

            <Container
                flow='column'
            >
                <Feature
                    icon={icons.objects.notes}
                    title='Notes'
                    subtitle='Save your notes'
                    margin={{
                        top: 3
                    }}
                />
                <Feature
                    icon={icons.objects.map}
                    title='Map'
                    subtitle='Track your route'
                    margin={{
                        top: 3,
                    }}
                />
                <Feature
                    icon={icons.objects.restoration}
                    title='Move back'
                    subtitle='Go back in time'
                    margin={{
                        top: 3,
                    }}
                />
            </Container>
        </Container>

        <Container
            flow='column'
            align={{
                cross: 'center',
            }}
            margin={{
                top: 4,
            }}
        >
            <I18NText
                intent='success'
                margin={{
                    top: 5
                }}
            >
                {`Price $${controller.price / 100} USD`}
            </I18NText>
            <Anchor
                flow='row'
                align={{
                    cross: 'center',
                }}
                intent='primary'
                href={controller.url}
                margin={{
                    top: 2
                }}
            >
                <Icon
                    component={icons.actions.pay}
                    size='xs'
                    margin={{
                        right: 1
                    }}
                />
                <I18NText>
                    Proceed to payment page
                </I18NText>
            </Anchor>
            <Container
                flow='row'
                align={{
                    cross: 'center',
                }}
                margin={{
                    top: 1
                }}
            >
                <Icon
                    component={icons.objects.locker}
                    size='xxs'
                />
                <I18NText
                    intent='secondary'
                    margin={{
                        left: 1
                    }}
                >
                    Powered by Stripe
                </I18NText>
            </Container>

            <I18NText
                intent='secondary'
                margin={{
                    top: 4
                }}
            >
                If you already bought the game once and want to recover your account, please contact us.
            </I18NText>
        </Container>
    </templates.layout>
});

type PaymentProps = {
    templates: {
        layout: FC<PropsWithChildren<UnnormalizedProps>>;
    };
};

const Feature = ({
    icon,
    title,
    subtitle,
    ...props
}: {
    icon: ReactElement;
    title: string;
    subtitle: string;
} & UnnormalizedProps) => {
    const icons = useIcons();

    return (
        <Container
            flow="row"
            align={{
                cross: 'center',
            }}
            {...props}
        >
            <Icon
                component={icons.states.ok}
                size='xs'
            />
            <Container
                margin={{
                    left: 1,
                }}
            >
                <Container
                    flow="row"
                    align={{
                        cross: 'center',
                    }}
                >
                    <Icon
                        component={icon}
                        size='xs'
                    />
                    <I18NText
                        margin={{
                            left: 0.5,
                        }}
                    >
                        {title}
                    </I18NText>
                </Container>

                <I18NText intent="secondary">{subtitle}</I18NText>
            </Container>
        </Container>
    );
};

// const CheckoutForm = () => {
//   const icons = useIcons();
//
//   const stripe = useStripe();
//   const elements = useElements();
//
//   const [message, setMessage] = useState<string | undefined>();
//   const [isProcessing, setIsProcessing] = useState(false);
//
//   const handleSubmit = async () => {
//     if (!stripe || !elements) {
//       return
//     }
//
//     setIsProcessing(true);
//
//     const {error} = await stripe.confirmPayment({
//       elements,
//       confirmParams: {
//         // Make sure to change this to your payment completion page
//         return_url: `${window.location.origin}/completion`,
//       },
//     });
//
//     if (error.type === "card_error" || error.type === "validation_error") {
//       setMessage(error.message);
//     } else {
//       setMessage("An unexpected error occured.");
//     }
//
//     setIsProcessing(false);
//   }
//
//   return <>
//     <Text
//       intent='title'
//     >
//       Buy premium version
//     </Text>
//
//     <Container
//       flow='row'
//       align={{
//         main: 'center',
//         cross: 'center'
//       }}
//       margin={{
//         top: 2
//       }}
//     >
//       <Icon
//         component={icons.states.secure}
//       />
//       <Text
//         intent='secondary'
//       >
//         Powered by Stripe
//       </Text>
//     </Container>
//
//     <Container
//       margin={{
//         top: 1
//       }}
//     >
//       <LinkAuthenticationElement/>
//       <PaymentElement />
//       {message && <Text
//         intent='danger'
//         margin={{
//           top: 1
//         }}
//       >
//         {message}
//       </Text>}
//     </Container>
//
//     {stripe && elements && <Button
//       intent="primary"
//       progress={isProcessing || !stripe || !elements}
//       onClick={handleSubmit}
//       margin={{
//         top: 2
//       }}
//     >
//       <Icon component={icons.actions.pay} />
//       <Text>Pay $4.99 USD</Text>
//     </Button>}
//   </>
// }

const AlreadyPaid = () => {
    return <Container>
        <Text>Ya tienes la version premium</Text>
    </Container>
}

export {Payment};
